"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useRef } from "react";

import { useChangeLocale } from "@/app/hooks/useChangeLocale";
import { useSession } from "@/app/hooks/useSession";
import { loadPreferences, savePreferences } from "@/lib/storage";
import { preferencesApi } from "@/lib/sync/client";
import type { UserPreferences } from "@/lib/types";

import { useOnlineStatus } from "./useOnlineStatus";

export function usePreferences() {
  const { isAuthenticated } = useSession();
  const { isOffline } = useOnlineStatus();
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const prevAuthState = useRef(isAuthenticated);
  const currentLocale = useLocale();
  const { changeLocale } = useChangeLocale();
  const lastSyncedLocale = useRef<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences> & { activeListId?: string | null }) => {
      // Don't attempt server sync when offline
      if (isOffline) {
        throw new Error("OFFLINE"); // Throw error to trigger onError but not make API call
      }
      return preferencesApi.updatePreferences(updates);
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["preferences"] });
      const previous = queryClient.getQueryData<UserPreferences & { activeListId?: string | null }>(
        ["preferences"]
      );
      queryClient.setQueryData(["preferences"], (old: any) => {
        const updated = {
          ...old,
          ...updates,
          // Optimistically set updatedAt for offline mode
          updatedAt: new Date().toISOString(),
        };
        savePreferences(updated);
        return updated;
      });
      return { previous };
    },
    onError: (err, variables, context) => {
      // Don't treat offline as an error - it's expected behavior
      if (err instanceof Error && err.message === "OFFLINE") {
        return;
      }
      console.error("❌ Failed to sync preferences to server:", err);
      if (context?.previous) {
        queryClient.setQueryData(["preferences"], context.previous);
        savePreferences(context.previous);
      }
    },
    onSuccess: (data) => {
      console.log("✅ Preferences synced to server");
      // Update with server's timestamp
      queryClient.setQueryData(["preferences"], data);
      savePreferences(data);
    },
  });

  const {
    data: serverPreferences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["preferences"],
    queryFn: async () => {
      const data = await preferencesApi.fetchPreferences();
      console.log("📥 Loaded preferences from server:", data);

      // Compare updatedAt timestamps to determine which preferences are newer
      const localPrefs = loadPreferences();

      // If no local updatedAt or server is newer, use server data
      if (!localPrefs.updatedAt || !data.updatedAt) {
        // No timestamp info - save server data and use it
        savePreferences(data);
        return data;
      }

      const localTime = new Date(localPrefs.updatedAt).getTime();
      const serverTime = new Date(data.updatedAt).getTime();

      if (serverTime > localTime) {
        // Server has newer preferences - use them
        console.log("🔄 Server preferences are newer, adopting server data");
        savePreferences(data);
        return data;
      } else if (localTime > serverTime) {
        // Local preferences are newer - we must sync them to server.
        // IMPORTANT: return the actual server data here (not localPrefs). Returning
        // localPrefs would make `serverPreferences` point to the local copy and the
        // effect that detects newer local prefs (below) would not run. By returning
        // the server data we ensure `serverPreferences` reflects the server and the
        // effect can compare timestamps and trigger the sync.
        console.log("🔄 Local preferences are newer than server; will trigger sync after load");
        // Do NOT overwrite local storage with server data here. We want to keep
        // localPrefs in localStorage until the sync finishes.
        return data;
      } else {
        // Same timestamp - use server data as source of truth
        savePreferences(data);
        return data;
      }
    },
    enabled: isAuthenticated && !isOffline,
    staleTime: Infinity, // Never refetch automatically
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting to network
    // Use localStorage as placeholder while server data loads
    placeholderData: () => {
      const cached = loadPreferences();
      return cached
        ? { ...cached, activeListId: (cached as any)?.activeListId ?? null }
        : undefined;
    },
  });

  // Sync local changes to server when preferences load
  useEffect(() => {
    // Signed out, there are no saved preferences: the address decides the language (a /cn page stays Chinese).
    if (!isAuthenticated) return;
    if (serverPreferences?.language) {
      const localPrefs = loadPreferences();

      // Check if local preferences are newer than server (need to sync)
      if (localPrefs.updatedAt && serverPreferences.updatedAt) {
        const localTime = new Date(localPrefs.updatedAt).getTime();
        const serverTime = new Date(serverPreferences.updatedAt).getTime();

        if (localTime > serverTime && !isOffline) {
          console.log(
            `🔄 Local preferences are newer (offline changes detected), syncing to server`
          );

          // Sync all local preferences to server
          const updates: any = {};
          if (localPrefs.theme) updates.theme = localPrefs.theme;
          if (localPrefs.language) updates.language = localPrefs.language;
          if ((localPrefs as any).activeListId !== undefined) {
            updates.activeListId = (localPrefs as any).activeListId;
          }

          // Set cookie to local language preference
          if (localPrefs.language) {
            document.cookie = `NEXT_LOCALE=${localPrefs.language}; path=/; max-age=31536000; SameSite=Lax`;

            // Ensure locale matches local preference
            if (currentLocale !== localPrefs.language) {
              console.log(`🌐 Switching to local preference: ${localPrefs.language}`);
              changeLocale(localPrefs.language);
            }
          }

          // Trigger sync to server
          if (isAuthenticated && Object.keys(updates).length > 0) {
            mutation.mutate(updates);
          }
          return;
        }
      }

      // Server preferences are current - use them
      document.cookie = `NEXT_LOCALE=${serverPreferences.language}; path=/; max-age=31536000; SameSite=Lax`;

      // Auto-switch locale if server preference differs from current locale
      // This ONLY happens on initial login when preferences first sync
      // After that, locale-aware navigation (from @/i18n/navigation) preserves the locale
      // Only switch if we haven't already switched to this locale (prevent loops during navigation)
      if (
        serverPreferences.language !== currentLocale &&
        lastSyncedLocale.current !== serverPreferences.language
      ) {
        console.log(
          `🌐 Auto-switching locale from ${currentLocale} to ${serverPreferences.language} (initial login)`
        );
        lastSyncedLocale.current = serverPreferences.language;
        changeLocale(serverPreferences.language);
      }
    }
  }, [
    serverPreferences?.language,
    serverPreferences?.updatedAt,
    currentLocale,
    changeLocale,
    isOffline,
    isAuthenticated,
    mutation,
  ]);

  // Clear cache when user logs out, refetch when user signs in
  useEffect(() => {
    const wasAuthenticated = prevAuthState.current;
    prevAuthState.current = isAuthenticated;

    if (!isAuthenticated && wasAuthenticated) {
      // User just logged out - clear cache and reset locale tracking
      queryClient.removeQueries({ queryKey: ["preferences"] });
      lastSyncedLocale.current = null;
    } else if (isAuthenticated && !wasAuthenticated) {
      // User just signed in - invalidate to trigger fresh fetch
      queryClient.invalidateQueries({ queryKey: ["preferences"] });
    }
  }, [isAuthenticated, queryClient]);

  const updatePreferences = useCallback(
    (updates: Partial<UserPreferences> & { activeListId?: string | null }, immediate = false) => {
      if (!isAuthenticated) {
        const current = loadPreferences();
        const updated = { ...current, ...updates };
        savePreferences(updated);
        queryClient.setQueryData(["preferences"], updated);
        return;
      }

      // Always include updatedAt timestamp when updating preferences
      const now = new Date().toISOString();
      queryClient.setQueryData(["preferences"], (old: any) => {
        const updated = { ...old, ...updates, updatedAt: now };
        savePreferences(updated);
        return updated;
      });

      // Don't attempt server sync when offline
      if (isOffline) {
        console.log("📴 Offline - preferences saved locally with timestamp, will sync when online");
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Language changes should be immediate to prevent race conditions with route changes
      if (immediate || updates.language !== undefined) {
        mutation.mutate(updates);
      } else {
        debounceTimerRef.current = setTimeout(() => {
          mutation.mutate(updates);
        }, 1000);
      }
    },
    [isAuthenticated, isOffline, mutation, queryClient]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const preferences = isAuthenticated
    ? (serverPreferences ?? loadPreferences())
    : loadPreferences();

  return {
    preferences: { ...preferences, activeListId: (preferences as any)?.activeListId ?? null },
    updatePreferences,
    isLoading: isAuthenticated && isLoading,
    isSyncing: mutation.isPending,
    error,
  };
}
